import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Phone, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { menuData, restaurantInfo } from '../data/menuData';

// Menu Item Card
const MenuItem = ({ item, showMenu = false }) => (
  <div 
    className="menu-card bg-[#0f0f0f] border border-white/5 rounded-xl p-4 md:p-5 hover:border-orange-500/30 transition-all"
    data-testid={`menu-item-${item.id}`}
  >
    <div className="flex gap-4">
      {item.image && (
        <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden flex-shrink-0">
          <img 
            src={item.image} 
            alt={item.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h4 className="font-accent text-base md:text-lg font-semibold text-white">
            {item.name}
          </h4>
          <div className="text-right flex-shrink-0">
            <span className="font-bold text-orange-500 text-lg">
              {item.price ? `${item.price.toFixed(2)}€` : item.priceExtra ? `+${item.priceExtra.toFixed(2)}€` : ''}
            </span>
            {showMenu && item.priceMenu && (
              <div className="text-xs text-gray-400">
                Menu: {item.priceMenu.toFixed(2)}€
              </div>
            )}
          </div>
        </div>
        {item.badge && (
          <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider mb-2 ${
            item.badge === 'populaire' ? 'badge-popular' : 
            item.badge === 'maison' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
            'badge-hot'
          }`}>
            {item.badge}
          </span>
        )}
        <p className="text-gray-400 text-sm line-clamp-2">{item.description}</p>
      </div>
    </div>
  </div>
);

// Menu Category Section
const MenuCategory = ({ category, showMenu = false }) => (
  <div className="space-y-4" data-testid={`menu-category-${category.title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="mb-6">
      <h3 className="font-heading text-2xl md:text-3xl text-white uppercase mb-2">
        {category.title}
      </h3>
      <p className="text-gray-400">{category.description}</p>
    </div>
    
    <div className="grid gap-3">
      {category.items.map((item) => (
        <MenuItem key={item.id} item={item} showMenu={showMenu} />
      ))}
    </div>

    {/* Options if available */}
    {category.options && (
      <div className="mt-6 p-4 bg-[#0A0A0A] rounded-xl border border-white/5">
        {category.options.viandes && (
          <div className="mb-4">
            <h5 className="font-accent text-sm font-semibold text-orange-400 uppercase tracking-wider mb-2">
              Viandes au choix
            </h5>
            <p className="text-gray-400 text-sm">
              {category.options.viandes.join(' • ')}
            </p>
          </div>
        )}
        {category.options.sauces && (
          <div className="mb-4">
            <h5 className="font-accent text-sm font-semibold text-orange-400 uppercase tracking-wider mb-2">
              Sauces au choix
            </h5>
            <p className="text-gray-400 text-sm">
              {category.options.sauces.join(' • ')}
            </p>
          </div>
        )}
        {category.options.supplements && (
          <div>
            <h5 className="font-accent text-sm font-semibold text-orange-400 uppercase tracking-wider mb-2">
              Suppléments
            </h5>
            <div className="flex flex-wrap gap-2">
              {category.options.supplements.map((sup, i) => (
                <span key={i} className="text-gray-400 text-sm">
                  {sup.name}: <span className="text-orange-500">{sup.price.toFixed(2)}€</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

export const MenuPage = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('crousty');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['crousty', 'bowls', 'tacos', 'extras', 'desserts', 'boissons', 'autres'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-[#050505] pt-20 md:pt-24" data-testid="menu-page">
      {/* Header */}
      <div className="px-4 md:px-8 py-8 md:py-12 bg-[#0A0A0A] border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Retour</span>
          </Link>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl text-white uppercase mb-2">
                Notre <span className="text-orange-500">Menu</span>
              </h1>
              <p className="text-gray-400">
                Découvrez toutes nos spécialités
              </p>
            </div>
            <a href={restaurantInfo.phoneLink} data-testid="menu-call-button">
              <Button className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider px-6 py-3 rounded-md flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Commander
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Menu Content */}
      <div className="px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-7xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* Tabs Navigation */}
            <TabsList className="flex flex-wrap gap-2 bg-transparent p-0 h-auto mb-8" data-testid="menu-tabs">
              <TabsTrigger 
                value="crousty" 
                className="tab-trigger px-4 py-2 rounded-md bg-[#0f0f0f] border border-white/10 text-gray-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-yellow-500 data-[state=active]:text-black data-[state=active]:border-transparent font-accent uppercase tracking-wider text-sm"
                data-testid="tab-crousty"
              >
                Crousty
              </TabsTrigger>
              <TabsTrigger 
                value="bowls" 
                className="tab-trigger px-4 py-2 rounded-md bg-[#0f0f0f] border border-white/10 text-gray-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-yellow-500 data-[state=active]:text-black data-[state=active]:border-transparent font-accent uppercase tracking-wider text-sm"
                data-testid="tab-bowls"
              >
                Bowls
              </TabsTrigger>
              <TabsTrigger 
                value="tacos" 
                className="tab-trigger px-4 py-2 rounded-md bg-[#0f0f0f] border border-white/10 text-gray-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-yellow-500 data-[state=active]:text-black data-[state=active]:border-transparent font-accent uppercase tracking-wider text-sm"
                data-testid="tab-tacos"
              >
                Tacos
              </TabsTrigger>
              <TabsTrigger 
                value="extras" 
                className="tab-trigger px-4 py-2 rounded-md bg-[#0f0f0f] border border-white/10 text-gray-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-yellow-500 data-[state=active]:text-black data-[state=active]:border-transparent font-accent uppercase tracking-wider text-sm"
                data-testid="tab-extras"
              >
                Extras
              </TabsTrigger>
              <TabsTrigger 
                value="desserts" 
                className="tab-trigger px-4 py-2 rounded-md bg-[#0f0f0f] border border-white/10 text-gray-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-yellow-500 data-[state=active]:text-black data-[state=active]:border-transparent font-accent uppercase tracking-wider text-sm"
                data-testid="tab-desserts"
              >
                Desserts
              </TabsTrigger>
              <TabsTrigger 
                value="boissons" 
                className="tab-trigger px-4 py-2 rounded-md bg-[#0f0f0f] border border-white/10 text-gray-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-yellow-500 data-[state=active]:text-black data-[state=active]:border-transparent font-accent uppercase tracking-wider text-sm"
                data-testid="tab-boissons"
              >
                Boissons
              </TabsTrigger>
              <TabsTrigger 
                value="autres" 
                className="tab-trigger px-4 py-2 rounded-md bg-[#0f0f0f] border border-white/10 text-gray-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-yellow-500 data-[state=active]:text-black data-[state=active]:border-transparent font-accent uppercase tracking-wider text-sm"
                data-testid="tab-autres"
              >
                Kebabs & Plus
              </TabsTrigger>
            </TabsList>

            {/* Tab Contents */}
            <TabsContent value="crousty" className="mt-0">
              <MenuCategory category={menuData.crousty} />
            </TabsContent>

            <TabsContent value="bowls" className="mt-0">
              <MenuCategory category={menuData.bowls} />
            </TabsContent>

            <TabsContent value="tacos" className="mt-0">
              <MenuCategory category={menuData.tacos} />
            </TabsContent>

            <TabsContent value="extras" className="mt-0">
              <MenuCategory category={menuData.extras} />
            </TabsContent>

            <TabsContent value="desserts" className="mt-0">
              <MenuCategory category={menuData.desserts} />
            </TabsContent>

            <TabsContent value="boissons" className="mt-0">
              <MenuCategory category={menuData.boissons} />
            </TabsContent>

            <TabsContent value="autres" className="mt-0 space-y-12">
              <MenuCategory category={menuData.sandwichs} showMenu={true} />
              <MenuCategory category={menuData.assiettes} />
              <MenuCategory category={menuData.burgers} showMenu={true} />
              <MenuCategory category={menuData.salades} />
              <MenuCategory category={menuData.menuEnfant} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="px-4 md:px-8 py-8 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400 mb-4">Prêt à commander ?</p>
          <a href={restaurantInfo.phoneLink} data-testid="menu-bottom-call-button">
            <Button className="bg-orange-500 hover:bg-orange-600 text-black font-bold uppercase tracking-wider px-8 py-4 rounded-md flex items-center gap-3 mx-auto glow-orange">
              <Phone className="w-5 h-5" />
              {restaurantInfo.phone}
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default MenuPage;
